import React, { useEffect, useMemo } from "react";
import { Grid, Box, Typography } from "@mui/material";
import { SectionHeader } from "../../ui/SectionHeader";
import idIcon from "../../../assets/svgIcons/id.svg";

const FundExpenseDetails = ({
  fields,
  renderField,
  formikRef,
  values,
}: any) => {
  const formValues = values || formikRef?.current?.values || {};

  useEffect(() => {
    const sourceLoanAmount = formikRef?.current?.values?.loanAmount;
    const requestedLoanAmount =
      formikRef?.current?.values?.fundRequestedLoanAmount;
      console.log(requestedLoanAmount, 'requestedLoanAmount')
    const isRequestedLoanAmountEmpty =
      requestedLoanAmount === undefined ||
      requestedLoanAmount === null ||
      String(requestedLoanAmount).trim() === "";

    if (
      sourceLoanAmount !== undefined &&
      sourceLoanAmount !== null &&
      String(sourceLoanAmount).trim() !== "" &&
      isRequestedLoanAmountEmpty
    ) {
      formikRef?.current?.setFieldValue(
        "fundRequestedLoanAmount",
        sourceLoanAmount,
        false
      );
    }
  }, [
    formikRef?.current?.values?.loanAmount
  ]);

  const sortedFields = useMemo(() => {
    if (!Array.isArray(fields)) return [];
    return [...fields].sort(
      (a: any, b: any) => (a?.fieldOrder ?? 0) - (b?.fieldOrder ?? 0),
    );
  }, [fields]);

  const getNormalizedFieldName = (name: any) =>
    String(name || "")
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");

  const startsWithAnyPrefix = (name: any, prefixes: string[]) => {
    const normalized = getNormalizedFieldName(name);
    return prefixes.some(
      (prefix) =>
        normalized.startsWith(prefix) || normalized.startsWith(`ad${prefix}`),
    );
  };

  const includesAnyKeyword = (value: any, keywords: string[]) => {
    const normalized = getNormalizedFieldName(value);
    return keywords.some((keyword) => normalized.includes(keyword));
  };

  const isExactTotalField = (value: any, keywords: string[]) => {
    const normalized = getNormalizedFieldName(value);
    return keywords.some(
      (keyword) =>
        normalized === keyword ||
        normalized.endsWith(keyword) ||
        normalized.startsWith(keyword),
    );
  };

  // Expense fields by prefix: expense* / adExpense*
  // and explicit Total Expense name/label matches.
  const expenseFields = useMemo(
    () =>
      sortedFields.filter((f: any) => {
        const byPrefix = startsWithAnyPrefix(f?.name, ["expense", "expenses"]);
        const byTotalExpenseText =
          includesAnyKeyword(f?.name, ["totalexpense", "expense"]) ||
          includesAnyKeyword(f?.label, ["totalexpense", "expense"]);
        return byPrefix || byTotalExpenseText;
      }),
    [sortedFields],
  );

  // Fund fields by prefix: fund* / fundRequest* / adFund*
  const fundFields = useMemo(
    () =>
      sortedFields.filter((f: any) =>
        startsWithAnyPrefix(f?.name, ["fund", "fundrequest", "funds"]),
      ),
    [sortedFields],
  );

  const isFilled = (value: any): boolean => {
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === "number") return true;
    if (typeof value === "boolean") return true;
    if (value && typeof value === "object") {
      if ("value" in value) return String(value.value ?? "").trim() !== "";
      return Object.keys(value).length > 0;
    }
    return String(value ?? "").trim() !== "";
  };

  const toNumericValue = (raw: any): number => {
    if (raw === null || raw === undefined || raw === "") return 0;

    if (typeof raw === "number") {
      return isNaN(raw) ? 0 : raw;
    }

    const candidate =
      typeof raw === "object" && raw !== null && "value" in raw
        ? (raw as any).value
        : raw;

    const normalized = String(candidate)
      .replace(/,/g, "")
      .replace(/[^0-9.-]/g, "")
      .trim();

    if (!normalized) return 0;
    const parsed = parseFloat(normalized);
    return isNaN(parsed) ? 0 : parsed;
  };

  const getTotalExpense = (type: "expense" | "fund"): string => {
    const activeFields = type === "expense" ? expenseFields : fundFields;
    const totalFieldKeywords =
      type === "expense"
        ? ["totalexpense", "expensetotal"]
        : ["totalfund", "fundtotal"];

    const directTotalField = activeFields.find(
      (f: any) =>
        isExactTotalField(f?.name, totalFieldKeywords) ||
        isExactTotalField(f?.label, totalFieldKeywords),
    );

    const keys = activeFields
      .map((f: any) => f?.name)
      .filter((name: string) => !!name && name !== directTotalField?.name);
    const total = keys.reduce((sum: number, key: string) => {
      const raw = formValues?.[key];
      return sum + toNumericValue(raw);
    }, 0);

    if (total > 0) {
      return `₹${total.toLocaleString("en-IN")}`;
    }

    if (directTotalField?.name) {
      const directRaw = formValues?.[directTotalField.name];
      const directValue = toNumericValue(directRaw);
      if (directValue > 0) {
        return `₹${directValue.toLocaleString("en-IN")}`;
      }
    }

    if (total === 0) return "—";
    return `₹${total.toLocaleString("en-IN")}`;
  };

  const admissionStatusValue =
    formValues?.admissionStatus && typeof formValues.admissionStatus === "object"
      ? formValues.admissionStatus?.value ?? formValues.admissionStatus?.label
      : formValues?.admissionStatus;
  const isAdmissionUndecided =
    String(admissionStatusValue ?? "").trim().toLowerCase() === "undecided";
  const fundExpenseOptionalKeys = new Set([
    "expenseTuitionFees",
    "expenseLivingExpenses",
    "expenseTravelExpenses",
    "expenseOtherExpenses",
    "fundRequestedLoanAmount",
    "fundOwnSources",
    "fundScholarshipAmount",
    "fundOtherIncome",
  ]);
  const getRenderField = (field: any) =>
    isAdmissionUndecided && fundExpenseOptionalKeys.has(field?.name)
      ? { ...field, required: false }
      : field;

  const getCompletion = (sectionFields: any[]) => {
    const requiredFields = sectionFields.filter(
      (f: any) => getRenderField(f)?.required !== false,
    );
    const filledCount = requiredFields.filter((field: any) =>
      isFilled(formValues?.[field?.name]),
    ).length;
    return `${filledCount} / ${requiredFields.length}`;
  };

  return (
    <>
      {/* ── Expense Details Section ────────────────────────────────────────── */}
      <Grid container mt={1} spacing={2} sx={{ px: { xs: 0, sm: 2 } }}>
        <Grid item xs={12}>
          <Box sx={{ mb: 1, mt: 1 }}>
            <SectionHeader
              icon={
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 40,
                    height: 40,
                    borderRadius: "8px",
                    bgcolor: "#e5feffff",
                  }}
                >
                  <img
                    src={idIcon}
                    alt="icon"
                    style={{ width: 28, height: 28 }}
                  />
                </Box>
              }
              title="Expense Details"
              subtitle={`Total Expense: ${getTotalExpense("expense")}`}
              completion={getCompletion(expenseFields)}
            />
          </Box>
        </Grid>

        {expenseFields.map((field: any) => (
          <Grid item xs={12} sm={6} key={field?.id || field?.name}>
            {renderField?.(getRenderField(field))}
          </Grid>
        ))}
      </Grid>

      {/* ── Fund Details Section ───────────────────────────────────────────── */}
      <Grid container mt={2} spacing={2} sx={{ px: { xs: 0, sm: 2 }, mb: 2 }}>
        <Grid item xs={12}>
          <Box sx={{ mb: 1, mt: 1 }}>
            <SectionHeader
              icon={
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 40,
                    height: 40,
                    borderRadius: "8px",
                    bgcolor: "#e5feffff",
                  }}
                >
                  <img
                    src={idIcon}
                    alt="icon"
                    style={{ width: 28, height: 28 }}
                  />
                </Box>
              }
              title="Fund Details"
              subtitle={`Total Fund: ${getTotalExpense("fund")}`}
              completion={getCompletion(fundFields)}
            />
          </Box>
        </Grid>

        {fundFields.map((field: any) => (
          <Grid item xs={12} sm={6} key={field?.id || field?.name}>
            {renderField?.(getRenderField(field))}
          </Grid>
        ))}
      </Grid>
    </>
  );
};

export default FundExpenseDetails;
